import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { accountrtk } from "api/api-accounts";
import { aiForm } from "api/api-aiform";
import { auth } from "api/api-auth";
import { banks } from "api/api-banks";
import { comments } from "api/api-comments";
import { companiesrtk } from "api/api-company";
import { compliance } from "api/api-compliance";
import { creditCommittee } from "api/api-credit-committee";
import { investor_v2 } from "api/api-invoices";
import { postcc } from "api/api-post-cc";
import { emailsrtk } from "api/api-emails";
import { configuration } from "api/api-configuration";
import { finance } from "api/api-finance";
import { invoiceFinanceApi } from "api/api-invoice-finance";
import { funder } from "api/api-funder";
import { guideline } from "api/api-guidelines";
import { human_resources } from "api/api-human-resources";
import { it } from "api/api-it";
import { legal } from "api/api-legal";
import { maps } from "api/api-maps";
import { marketing } from "api/api-marketing";
import { marketplace } from "api/api-marketplace";
import { misc } from "api/api-misc";
import { operations } from "api/api-operations";
import { origination } from "api/api-origination";
import { risk } from "api/api-risk";
import { notes } from "api/api-accounts-notes-attachements";
import { riskDashboardApi } from "api/api-risk-dashboard";
import { sales } from "api/api-sales";
import { support } from "api/api-support";
import { users } from "api/api-users";
import { users_v2 } from "api/api-users-v2";
import { vendors } from "api/api-vendors";
import { vendorsv2 } from "api/api-vendorsv2";
import { countries } from "api/api-countries";
import { statuses } from "api/api-status";
import { persistReducer, persistStore } from "redux-persist";
import storage from "redux-persist/lib/storage";
import { unauthenticatedMiddleware } from "./middleware/unauthenticatedMiddleware";
import appStatesReducer from "./reducers/appStatesReducer";
import cargoReducer from "./reducers/cargoReducer";
import companyReducer from "./reducers/companyReducer";
import countriesReducer from "./reducers/countriesReducer"; // Keep existing countries reducer
import creditLimitsReducer from "./reducers/creditLimitsReducer";
import experimentalFeaturesReducer from "./reducers/experimentalFeaturesReducer";
import sofrRatesReducer from "./reducers/sofrReducer";
import stepsReducer from "./reducers/stepsReducer";
import searchReducer from "./reducers/searchReducer";
import { audittrail } from "api/api-audittrail";
import { statusMasterApi } from "api/api-category-status";
import { ewalletApi } from "../api/api-ewallet";
import { investor } from "../api/api-investor";
import { invoiceOnboardingApi } from "../api/invoice-onboarding-api";
import { leadComments } from "api/api-lead-comments";
import { notifications } from "api/api-notifications";
import invoiceSliceReducer from "./reducers/multipleInvoiceReducer";

// Combine all reducers
const rootReducer = combineReducers({
  companies: companyReducer,
  creditLimits: creditLimitsReducer,
  countries: countriesReducer, // Existing countries reducer (keep this)
  steps: stepsReducer,
  appState: appStatesReducer,
  sofrRates: sofrRatesReducer,
  cargo: cargoReducer,
  experimentalFeatures: experimentalFeaturesReducer,
  search: searchReducer,
  selectInvoice: invoiceSliceReducer,

  // RTK Query API reducers
  [countries.reducerPath]: countries.reducer,
  [statuses.reducerPath]: statuses.reducer,
  [aiForm.reducerPath]: aiForm.reducer,
  [statusMasterApi.reducerPath]: statusMasterApi.reducer,
  [audittrail.reducerPath]: audittrail.reducer,
  [sales.reducerPath]: sales.reducer,
  [misc.reducerPath]: misc.reducer,
  [users.reducerPath]: users.reducer,
  [users_v2.reducerPath]: users_v2.reducer,
  [companiesrtk.reducerPath]: companiesrtk.reducer,
  [accountrtk.reducerPath]: accountrtk.reducer,
  [banks.reducerPath]: banks.reducer,
  [vendors.reducerPath]: vendors.reducer,
  [origination.reducerPath]: origination.reducer,
  [vendorsv2.reducerPath]: vendorsv2.reducer,
  [marketplace.reducerPath]: marketplace.reducer,
  [creditCommittee.reducerPath]: creditCommittee.reducer,
  [it.reducerPath]: it.reducer,
  [finance.reducerPath]: finance.reducer,
  [invoiceFinanceApi.reducerPath]: invoiceFinanceApi.reducer,
  [funder.reducerPath]: funder.reducer,
  [marketing.reducerPath]: marketing.reducer,
  [legal.reducerPath]: legal.reducer,
  [risk.reducerPath]: risk.reducer,
  [notes.reducerPath]: notes.reducer,
  [riskDashboardApi.reducerPath]: riskDashboardApi.reducer,
  [operations.reducerPath]: operations.reducer,
  [support.reducerPath]: support.reducer,
  [auth.reducerPath]: auth.reducer,
  [maps.reducerPath]: maps.reducer,
  [compliance.reducerPath]: compliance.reducer,
  [emailsrtk.reducerPath]: emailsrtk.reducer,
  [investor_v2.reducerPath]: investor_v2.reducer,
  [comments.reducerPath]: comments.reducer,
  [human_resources.reducerPath]: human_resources.reducer,
  [notifications.reducerPath]: notifications.reducer,
  [configuration.reducerPath]: configuration.reducer,
  [guideline.reducerPath]: guideline.reducer,
  [postcc.reducerPath]: postcc.reducer,
  [ewalletApi.reducerPath]: ewalletApi.reducer,
  [investor.reducerPath]: investor.reducer,
  [invoiceOnboardingApi.reducerPath]: invoiceOnboardingApi.reducer,
  [leadComments.reducerPath]: leadComments.reducer,
});

const persistedReducer = persistReducer(
  {
    key: "root",
    storage,
    // Persist UI slices only. Never restore RTK Query or auth caches after refresh.
    whitelist: [
      "companies",
      "creditLimits",
      "countries",
      "steps",
      "appState",
      "sofrRates",
      "cargo",
      "experimentalFeatures",
      "search",
      "selectInvoice",
    ],
  },
  rootReducer
);

// Configure store with middleware
export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      immutableCheck: false,
      serializableCheck: false,
      actionCreatorCheck: false,
    }).concat([
      unauthenticatedMiddleware,
      countries.middleware,
      statuses.middleware,
      aiForm.middleware,
      audittrail.middleware,
      sales.middleware,
      origination.middleware,
      misc.middleware,
      users.middleware,
      users_v2.middleware,
      companiesrtk.middleware,
      accountrtk.middleware,
      configuration.middleware,
      banks.middleware,
      notifications.middleware,
      vendors.middleware,
      vendorsv2.middleware,
      marketplace.middleware,
      creditCommittee.middleware,
      it.middleware,
      finance.middleware,
      invoiceFinanceApi.middleware,
      funder.middleware,
      marketing.middleware,
      legal.middleware,
      risk.middleware,
      notes.middleware,
      riskDashboardApi.middleware,
      operations.middleware,
      support.middleware,
      auth.middleware,
      maps.middleware,
      compliance.middleware,
      emailsrtk.middleware,
      investor_v2.middleware,
      postcc.middleware,
      comments.middleware,
      human_resources.middleware,
      guideline.middleware,
      audittrail.middleware,
      statusMasterApi.middleware,
      ewalletApi.middleware,
      investor.middleware,
      invoiceOnboardingApi.middleware,
      leadComments.middleware,
    ]),
  devTools: import.meta.env.VITE_ENV !== "main",
});

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export const persistor = persistStore(store);
